import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, DollarSign, TrendingUp, Search, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProventosTemplate({ getAuthHeaders }) {
  const [proventos, setProventos] = useState([]);
  const [origensProventos, setOrigensProventos] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNovoProvento, setShowNovoProvento] = useState(false);
  const [showNovaOrigem, setShowNovaOrigem] = useState(false);
  const [editingProvento, setEditingProvento] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
  const [filtroMes, setFiltroMes] = useState(new Date().getMonth() + 1);
  const [editingInline, setEditingInline] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  
  const [novoProvento, setNovoProvento] = useState({
    ano: new Date().getFullYear(),
    mes: new Date().getMonth() + 1,
    origem_id: '',
    origem_nome: '',
    moeda: '',
    valor: ''
  });

  const [novaOrigem, setNovaOrigem] = useState({
    nome: '',
    moeda: 'R$'
  });

  const meses = [
    { num: 1, nome: 'Janeiro' },
    { num: 2, nome: 'Fevereiro' },
    { num: 3, nome: 'Março' },
    { num: 4, nome: 'Abril' },
    { num: 5, nome: 'Maio' },
    { num: 6, nome: 'Junho' },
    { num: 7, nome: 'Julho' },
    { num: 8, nome: 'Agosto' },
    { num: 9, nome: 'Setembro' },
    { num: 10, nome: 'Outubro' },
    { num: 11, nome: 'Novembro' },
    { num: 12, nome: 'Dezembro' }
  ];

  useEffect(() => {
    carregarDados();
    inicializarOrigens();
  }, []);

  const inicializarOrigens = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/origens-proventos/inicializar`, {}, headers);
    } catch (error) {
      // Ignorar erro se já inicializado
      console.log('Origens já inicializadas ou erro:', error.message);
    }
  };

  const carregarDados = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const [proventosRes, origensRes, statsRes] = await Promise.all([
        axios.get(`${API}/proventos`, headers),
        axios.get(`${API}/origens-proventos`, headers),
        axios.get(`${API}/proventos/estatisticas`, headers)
      ]);
      
      setProventos(proventosRes.data);
      setOrigensProventos(origensRes.data);
      setEstatisticas(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvarProvento = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      
      if (editingProvento) {
        await axios.put(
          `${API}/proventos/${editingProvento.id}`,
          { valor: parseFloat(novoProvento.valor) },
          headers
        );
        toast.success('Provento atualizado com sucesso');
      } else {
        // Recarregar dados atualizados antes de validar
        const proventosAtualizados = await axios.get(`${API}/proventos`, headers);
        
        // Validar se já existe este provento neste mês
        const jaExiste = proventosAtualizados.data.some(p => 
          p.ano === parseInt(novoProvento.ano) && 
          p.mes === parseInt(novoProvento.mes) && 
          p.origem_nome === novoProvento.origem_nome
        );
        
        if (jaExiste) {
          toast.error('Este tipo de provento já foi registrado para este mês. Edite ou exclua diretamente na tabela.');
          return;
        }
        
        await axios.post(
          `${API}/proventos`,
          {
            ...novoProvento,
            valor: parseFloat(novoProvento.valor)
          },
          headers
        );
        toast.success('Provento criado com sucesso');
      }
      
      setShowNovoProvento(false);
      setEditingProvento(null);
      setNovoProvento({
        ano: new Date().getFullYear(),
        mes: new Date().getMonth() + 1,
        origem_id: '',
        origem_nome: '',
        moeda: '',
        valor: ''
      });
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar provento:', error);
      toast.error('Erro ao salvar provento');
    }
  };

  const handleSalvarOrigem = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/origens-proventos`, novaOrigem, headers);
      toast.success('Origem criada com sucesso');
      setShowNovaOrigem(false);
      setNovaOrigem({ nome: '', moeda: 'R$' });
      carregarDados();
    } catch (error) {
      console.error('Erro ao criar origem:', error);
      toast.error('Erro ao criar origem');
    }
  };

  const handleExcluirProvento = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este provento?')) {
      try {
        const headers = getAuthHeaders();
        await axios.delete(`${API}/proventos/${id}`, headers);
        toast.success('Provento excluído com sucesso');
        carregarDados();
      } catch (error) {
        console.error('Erro ao excluir provento:', error);
        toast.error('Erro ao excluir provento');
      }
    }
  };

  const handleExcluirMesCompleto = async (ids) => {
    if (window.confirm('Tem certeza que deseja excluir todos os proventos deste mês?')) {
      try {
        const headers = getAuthHeaders();
        await Promise.all(ids.map(id => axios.delete(`${API}/proventos/${id}`, headers)));
        toast.success('Mês excluído com sucesso');
        carregarDados();
      } catch (error) {
        console.error('Erro ao excluir mês:', error);
        toast.error('Erro ao excluir mês');
      }
    }
  };

  const handleEditarProvento = (provento) => {
    setEditingProvento(provento);
    setNovoProvento({
      ano: provento.ano,
      mes: provento.mes,
      origem_id: provento.origem_id,
      origem_nome: provento.origem_nome,
      moeda: provento.moeda,
      valor: provento.valor.toString()
    });
    setShowNovoProvento(true);
  };

  const handleOrigemChange = (origemId) => {
    const origem = origensProventos.find(o => o.id === origemId);
    if (origem) {
      setNovoProvento({
        ...novoProvento,
        origem_id: origem.id,
        origem_nome: origem.nome,
        moeda: origem.moeda
      });
    }
  };

  // Organizar proventos por ano/mês - UMA linha por mês
  const getProventosPorMes = () => {
    const mesesComProventos = {};
    
    proventos.forEach(p => {
      const chave = `${p.ano}-${p.mes}`;
      if (!mesesComProventos[chave]) {
        mesesComProventos[chave] = {
          ano: p.ano,
          mes: p.mes,
          fontes: {},
          ids: []
        };
      }
      
      // Armazenar o provento na fonte correspondente
      if (!mesesComProventos[chave].fontes[p.origem_nome]) {
        mesesComProventos[chave].fontes[p.origem_nome] = {
          valor: p.valor,
          moeda: p.moeda,
          id: p.id
        };
      }
      
      // Guardar todos os IDs de proventos deste mês
      mesesComProventos[chave].ids.push(p.id);
    });

    return Object.values(mesesComProventos).sort((a, b) => {
      if (a.ano !== b.ano) return b.ano - a.ano;
      return b.mes - a.mes;
    });
  };

  // Obter todas as fontes únicas que foram usadas
  const getFontesUsadas = () => {
    const fontesSet = new Set();
    proventos.forEach(p => fontesSet.add(p.origem_nome));
    return Array.from(fontesSet);
  };

  const formatCurrency = (value, moeda) => {
    if (!value) return moeda === 'R$' ? 'R$ 0,00' : '$ 0.00';
    
    if (moeda === 'R$') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    }
  };

  const mesesData = getProventosPorMes();
  const fontesUsadas = getFontesUsadas();

  // Filtrar por pesquisa
  const mesesFiltrados = mesesData.filter(m => {
    if (!searchTerm) return true;
    const mesNome = meses.find(mes => mes.num === m.mes)?.nome.toLowerCase() || '';
    return mesNome.includes(searchTerm.toLowerCase()) || 
           m.ano.toString().includes(searchTerm) ||
           fontesUsadas.some(f => f.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  return (
    <div className="space-y-6">
      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Recebido R$ */}
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg mr-4">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Recebido (R$)</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(estatisticas?.total_real_mes || 0, 'R$')}
              </p>
            </div>
          </div>
        </div>

        {/* Total Recebido $ */}
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg mr-4">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Recebido ($)</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(estatisticas?.total_dolar_mes || 0, '$')}
              </p>
            </div>
          </div>
        </div>

        {/* Média 12 Meses */}
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-lg mr-4">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium">Média Últimos 12 Meses</p>
              <p className="text-lg font-bold text-gray-900">
                {formatCurrency(estatisticas?.media_real_12m || 0, 'R$')} / {formatCurrency(estatisticas?.media_dolar_12m || 0, '$')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por fonte, mês ou ano..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
          
          <button
            onClick={() => setShowNovaOrigem(true)}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nova Fonte
          </button>
          
          <button
            onClick={() => setShowNovoProvento(true)}
            className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Novo Provento
          </button>
        </div>
      </div>

      {/* Tabela de Proventos */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ano
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mês
                </th>
                {fontesUsadas.map(fonte => {
                  const origem = origensProventos.find(o => o.nome === fonte);
                  return (
                    <th key={fonte} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {origem?.moeda === 'R$' ? '🇧🇷' : '🇺🇸'} {fonte}
                    </th>
                  );
                })}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={fontesUsadas.length + 3} className="px-6 py-4 text-center text-gray-500">
                    Carregando...
                  </td>
                </tr>
              ) : mesesFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={fontesUsadas.length + 3} className="px-6 py-4 text-center text-gray-500">
                    Nenhum provento cadastrado
                  </td>
                </tr>
              ) : (
                mesesFiltrados.map((mesData, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {mesData.ano}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {meses.find(m => m.num === mesData.mes)?.nome}
                    </td>
                    {fontesUsadas.map(fonte => {
                      const dados = mesData.fontes[fonte];
                      const isEditing = editingInline === dados?.id;
                      
                      return (
                        <td key={fonte} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {dados ? (
                            <div className="flex items-center justify-between group">
                              {isEditing ? (
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editingValues[dados.id] !== undefined ? editingValues[dados.id] : dados.valor}
                                    onChange={(e) => setEditingValues({ 
                                      ...editingValues, 
                                      [dados.id]: parseFloat(e.target.value) 
                                    })}
                                    className="w-24 px-2 py-1 border border-emerald-500 rounded focus:ring-2 focus:ring-emerald-500"
                                    autoFocus
                                  />
                                  <button
                                    onClick={async () => {
                                      try {
                                        const headers = getAuthHeaders();
                                        await axios.put(
                                          `${API}/proventos/${dados.id}`,
                                          { valor: editingValues[dados.id] !== undefined ? editingValues[dados.id] : dados.valor },
                                          headers
                                        );
                                        toast.success('Provento atualizado');
                                        setEditingInline(null);
                                        setEditingValues({});
                                        carregarDados();
                                      } catch (error) {
                                        toast.error('Erro ao atualizar');
                                      }
                                    }}
                                    className="text-green-600 hover:text-green-800"
                                    title="Salvar"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingInline(null);
                                      setEditingValues({});
                                    }}
                                    className="text-gray-600 hover:text-gray-800"
                                    title="Cancelar"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <span 
                                    className="font-medium cursor-pointer"
                                    onClick={() => {
                                      setEditingInline(dados.id);
                                      setEditingValues({ [dados.id]: dados.valor });
                                    }}
                                  >
                                    {formatCurrency(dados.valor, dados.moeda)}
                                  </span>
                                  <button
                                    onClick={() => handleExcluirProvento(dados.id)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 ml-2"
                                    title="Excluir este provento"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => handleExcluirMesCompleto(mesData.ids)}
                        className="text-red-600 hover:text-red-800"
                        title="Excluir mês completo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Novo Provento */}
      {showNovoProvento && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingProvento ? 'Editar Provento' : 'Novo Provento'}
              </h3>
              <button
                onClick={() => {
                  setShowNovoProvento(false);
                  setEditingProvento(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSalvarProvento} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ano</label>
                <input
                  type="number"
                  value={novoProvento.ano}
                  onChange={(e) => setNovoProvento({ ...novoProvento, ano: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  required
                  disabled={editingProvento}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mês</label>
                <select
                  value={novoProvento.mes}
                  onChange={(e) => setNovoProvento({ ...novoProvento, mes: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  required
                  disabled={editingProvento}
                >
                  {meses.map(m => (
                    <option key={m.num} value={m.num}>{m.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fonte de Provento</label>
                <select
                  value={novoProvento.origem_id}
                  onChange={(e) => handleOrigemChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  required
                  disabled={editingProvento}
                >
                  <option value="">Selecione...</option>
                  {origensProventos
                    .filter((origem, index, self) => 
                      index === self.findIndex((o) => o.nome === origem.nome && o.moeda === origem.moeda)
                    )
                    .map(origem => (
                      <option key={origem.id} value={origem.id}>
                        {origem.moeda === 'R$' ? '🇧🇷' : '🇺🇸'} {origem.nome} ({origem.moeda})
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor Recebido</label>
                <input
                  type="number"
                  step="0.01"
                  value={novoProvento.valor}
                  onChange={(e) => setNovoProvento({ ...novoProvento, valor: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowNovoProvento(false);
                    setEditingProvento(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
                >
                  {editingProvento ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Nova Fonte */}
      {showNovaOrigem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Nova Fonte de Provento</h3>
              <button
                onClick={() => setShowNovaOrigem(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSalvarOrigem} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Fonte</label>
                <input
                  type="text"
                  value={novaOrigem.nome}
                  onChange={(e) => setNovaOrigem({ ...novaOrigem, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Dividendos PETR4"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Moeda</label>
                <select
                  value={novaOrigem.moeda}
                  onChange={(e) => setNovaOrigem({ ...novaOrigem, moeda: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="R$">🇧🇷 Reais (R$)</option>
                  <option value="$">🇺🇸 Dólar ($)</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNovaOrigem(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProventosTemplate;
